import {
  Alert,
  Form,
  InputNumber,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  notification,
} from "antd";
import { useContext, useEffect, useState } from "react";

import "../styles.css";

import { Context } from "../../../context";
import { Fetch, Put } from "../../../fetch";
import { convertIdToKey } from "../../../utils";
import { putSeriesCoinFields } from "../coinsApi";

const { Column } = Table;

/** Blank -> null = "not configured" (backend $unsets the field). */
const normalizeCount = (value) =>
  value == null || value === "" ? null : Number(value);

/**
 * CMS-managed Watch & Earn tasks: per-series "watch X episodes today → claim
 * R coins" (daily repeat). Master switch lives in tasks_config
 * watch_earn_config.enabled; X/R live on the webseries doc
 * (watchEarnEpisodes / watchEarnCoins). Claims are validated by setu against
 * watch history and credited into the coin wallet server-side.
 */
const WatchEarnConfig = () => {
  const { token, apiUrl } = useContext(Context);

  const [series, setSeries] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [savingEnabled, setSavingEnabled] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [seriesId, setSeriesId] = useState();
  const [seriesTitle, setSeriesTitle] = useState("");
  const [counter, setCounter] = useState(0);

  const [form] = Form.useForm();

  useEffect(() => {
    Fetch(`${apiUrl}/v4/webseries?pageSize=100000`, token, (result) => {
      setSeries(convertIdToKey(result.webseries || []));
    });
  }, [apiUrl, token, counter]);

  useEffect(() => {
    Fetch(
      `${apiUrl}/v4/tasks-config/config/watch_earn_config`,
      token,
      (result) => {
        if (result && result.success !== false) {
          setEnabled(Boolean((result.data || {}).enabled));
        }
      }
    );
  }, [apiUrl, token]);

  const handleEnabledToggle = (checked) => {
    setSavingEnabled(true);
    setEnabled(checked); // optimistic
    Put(
      `${apiUrl}/v4/tasks-config/config/watch_earn_config`,
      token,
      { data: { enabled: checked } },
      (result) => {
        setSavingEnabled(false);
        if (result.success) {
          notification.success({
            message: "Success",
            description: `Watch & earn tasks ${checked ? "enabled" : "disabled"}`,
          });
        } else {
          setEnabled(!checked);
          notification.error({
            message: "Error updating watch & earn config",
            description: "Check console for more details",
          });
        }
      },
      () => {
        setSavingEnabled(false);
        setEnabled(!checked);
        notification.error({
          message: "Error updating watch & earn config",
          description: "Check console for more details",
        });
      }
    );
  };

  const showEditModal = (record) => {
    setSeriesId(record.key);
    setSeriesTitle(record.title);
    setModalVisible(true);
    form.setFieldsValue({
      watchEarnEpisodes: record.watchEarnEpisodes ?? null,
      watchEarnCoins: record.watchEarnCoins ?? null,
      watchEarnMaxClaims: record.watchEarnMaxClaims ?? null,
    });
  };

  const closeModal = () => {
    setModalVisible(false);
    setSeriesId(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const fields = await form.validateFields();
      const episodes = normalizeCount(fields.watchEarnEpisodes);
      const coins = normalizeCount(fields.watchEarnCoins);
      const maxClaims = normalizeCount(fields.watchEarnMaxClaims);
      // Both-or-neither: a half-configured series never shows a task.
      if ((episodes == null) !== (coins == null)) {
        notification.error({
          message: "Both fields required",
          description:
            "Set BOTH the episode count and the coin reward, or clear both to remove the task.",
        });
        return;
      }
      if (maxClaims != null && episodes == null) {
        notification.error({
          message: "Max claims needs a task",
          description:
            "Max claims per user only applies when the task (episodes + reward) is configured.",
        });
        return;
      }
      setConfirmLoading(true);

      putSeriesCoinFields(
        apiUrl,
        token,
        seriesId,
        {
          watchEarnEpisodes: episodes,
          watchEarnCoins: coins,
          // Clearing the task also clears the cap.
          watchEarnMaxClaims: episodes == null ? null : maxClaims,
        },
        (result) => {
          setConfirmLoading(false);
          if (result.success) {
            notification.success({
              message: "Success",
              description: "Watch & earn task updated",
            });
            closeModal();
            setCounter((c) => c + 1);
          } else {
            notification.error({
              message: "Error updating watch & earn task",
              description: "Check console for more details",
            });
          }
        },
        () => {
          setConfirmLoading(false);
          notification.error({
            message: "Error updating watch & earn task",
            description: "Check console for more details",
          });
        }
      );
    } catch (err) {
      notification.error({
        message: "Error updating watch & earn task",
        description: "Fields are incorrect/missing",
      });
    }
  };

  return (
    <div className="manage-tasks-wrapper">
      <Alert
        className="manage-tasks-caption"
        type="info"
        showIcon
        message="Watch X episodes of a series → R coins are claimed automatically and auto-spent on unlocking the next episodes. Progress carries over days and repeats after every claim (each episode counts once per day). Optional 'Max claims per user' caps how many rounds one viewer can complete. Credits are validated server-side (subject to the daily earn cap). See the How It Works page for the full picture."
      />
      <Space style={{ marginBottom: 16 }}>
        <span>Master switch</span>
        <Switch
          checked={enabled}
          loading={savingEnabled}
          onChange={handleEnabledToggle}
        />
      </Space>
      <Table dataSource={series}>
        <Column title="Title" dataIndex="title" key="title" align="center" />
        <Column
          title="Watch (episodes/day)"
          dataIndex="watchEarnEpisodes"
          key="watchEarnEpisodes"
          align="center"
          render={(v) => v ?? "-"}
        />
        <Column
          title="Reward (coins)"
          dataIndex="watchEarnCoins"
          key="watchEarnCoins"
          align="center"
          render={(v) => v ?? "-"}
        />
        <Column
          title="Max claims / user"
          dataIndex="watchEarnMaxClaims"
          key="watchEarnMaxClaims"
          align="center"
          render={(v) => v ?? "∞"}
        />
        <Column
          title="Task"
          key="configured"
          align="center"
          render={(text, record) => {
            if (record.watchLadderEnabled) {
              return <Tag color="orange">Ladder overrides</Tag>;
            }
            return record.watchEarnEpisodes > 0 && record.watchEarnCoins > 0 ? (
              <Tag color="green">Configured</Tag>
            ) : (
              <Tag>—</Tag>
            );
          }}
        />
        <Column
          title="Actions"
          key="actions"
          align="center"
          render={(text, record) => (
            <Space size="middle">
              <Typography.Link onClick={() => showEditModal(record)}>
                Edit
              </Typography.Link>
            </Space>
          )}
        />
      </Table>
      <Modal
        title={`Watch & Earn — ${seriesTitle}`}
        visible={modalVisible}
        onOk={handleSubmit}
        okText="Submit"
        onCancel={closeModal}
        confirmLoading={confirmLoading}
        destroyOnClose={true}
        maskClosable={false}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="watchEarnEpisodes"
            label="Watch episodes per day (X)"
            extra="Episodes the user must watch that day to claim. Blank (with Reward blank too) removes the task."
            rules={[
              {
                type: "integer",
                min: 1,
                message: "Watch episodes must be an integer >= 1",
              },
            ]}
          >
            <InputNumber min={1} precision={0} placeholder="e.g. 3" />
          </Form.Item>
          <Form.Item
            name="watchEarnCoins"
            label="Reward (coins, R)"
            extra="Coins credited per claim — align with the series' bundle price so watching funds unlocking"
            rules={[
              {
                type: "integer",
                min: 1,
                message: "Reward coins must be an integer >= 1",
              },
            ]}
          >
            <InputNumber min={1} precision={0} placeholder="e.g. 150" />
          </Form.Item>
          <Form.Item
            name="watchEarnMaxClaims"
            label="Max claims per user (optional)"
            extra="How many times ONE viewer can complete this series' task. Blank = unlimited (the watch → earn → unlock loop runs to the end of the series). At the cap the card disappears for that viewer."
            rules={[
              {
                type: "integer",
                min: 1,
                message: "Max claims must be an integer >= 1",
              },
            ]}
          >
            <InputNumber min={1} precision={0} placeholder="unlimited" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WatchEarnConfig;
